import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listRecentTeachings } from "@/lib/teachings.functions";
import {
  listContinueWatching,
  listMyFavorites,
  listRecentlyWatched,
  getMyProfile,
} from "@/lib/user.functions";
import { TeachingCard } from "@/components/teaching-card";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import { EmailSyncBanner } from "@/components/email-sync-banner";
import { ArrowRight, BookOpen, PlayCircle, Heart, History } from "lucide-react";
import heroImg from "@/assets/opt/hero.jpg";
import type { EmailSyncStatus } from "@/lib/email-sync-policy";

export const Route = createFileRoute("/_authenticated/inicio")({
  head: () => ({
    meta: [
      { title: "Início · Área de Ensinos Beit Tefilah Rechovot" },
      {
        name: "description",
        content:
          "Painel do membro: últimos ensinos publicados, Parashá da semana, transmissões ao vivo e seus favoritos.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Início · Área de Ensinos Beit Tefilah Rechovot" },
      {
        property: "og:description",
        content:
          "Painel do membro com os últimos ensinos, a Parashá da semana e as transmissões da congregação.",
      },
      { name: "twitter:title", content: "Início · Área de Ensinos Beit Tefilah Rechovot" },
      {
        name: "twitter:description",
        content:
          "Painel do membro com os últimos ensinos, a Parashá da semana e as transmissões da congregação.",
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  const recentFn = useServerFn(listRecentTeachings);
  const continueFn = useServerFn(listContinueWatching);
  const favFn = useServerFn(listMyFavorites);
  const historyFn = useServerFn(listRecentlyWatched);
  const profileFn = useServerFn(getMyProfile);
  const navigate = useNavigate();

  const { data: profile } = useQuery({ queryKey: ["my-profile"], queryFn: () => profileFn() });
  const { data: recent, isLoading } = useQuery({
    queryKey: ["recent-teachings"],
    queryFn: () => recentFn(),
  });
  const { data: continueList } = useQuery({
    queryKey: ["continue-watching"],
    queryFn: () => continueFn(),
  });
  const { data: favs } = useQuery({ queryKey: ["my-favorites"], queryFn: () => favFn() });
  const { data: history } = useQuery({
    queryKey: ["recently-watched"],
    queryFn: () => historyFn(),
  });

  const firstName = (profile?.full_name ?? "").trim().split(/\s+/)[0] || "membro";

  return (
    <div className="space-y-12">
      <EmailSyncBanner status={profile?.email_sync_status as EmailSyncStatus | undefined} />
      <section className="overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary-glow text-primary-foreground">
        <div className="grid gap-0 md:grid-cols-[1.1fr_0.9fr]">
          <div className="flex flex-col justify-center px-6 py-10 md:px-12 md:py-14">
            <div className="mb-4 flex items-center gap-3">
              <Logo variant="mark" size={44} />
              <p className="text-sm uppercase tracking-widest text-primary-foreground">
                Beit Tefilah Rechovot
              </p>
            </div>
            <h1 className="font-serif text-3xl font-semibold leading-tight md:text-5xl">
              Shalom, {firstName}
            </h1>
            <p className="mt-4 max-w-xl text-primary-foreground/90">
              Todos os estudos ministrados na Beit Tefilah Rechovot, organizados por Parashá, data e
              festividades. Navegue livremente.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" variant="secondary" onClick={() => navigate({ to: "/ensinos" })}>
                Explorar catálogo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="bg-transparent text-primary-foreground border-primary-foreground/40 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              >
                <Link to="/parashiot">
                  <BookOpen className="mr-2 h-4 w-4" /> Por Parashá
                </Link>
              </Button>
            </div>
          </div>
          <div className="relative min-h-[260px] md:min-h-full">
            <img
              src={heroImg}
              width={1200}
              height={796}
              fetchPriority="high"
              alt="Placas com Isaías 41:19-20 no jardim da Beit Tefilah Rechovot"
              className="absolute inset-0 h-full w-full object-cover object-center"
              style={{ objectPosition: "center 40%" }}
              loading="eager"
              decoding="async"
            />
            {/* Soft edge blend into the primary panel, without hiding the placas */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-primary/70 to-transparent md:w-32" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-primary/40 to-transparent md:hidden" />
          </div>
        </div>
      </section>

      {(history?.length ?? 0) > 0 && (
        <section>
          <div className="mb-6 flex items-end justify-between">
            <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-primary">
              <History className="h-6 w-6 text-gold" /> Últimos 5 ensinos assistidos
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {history!.map((t: any) => (
              <TeachingCard key={t.id} teaching={t} />
            ))}
          </div>
        </section>
      )}

      {(continueList?.length ?? 0) > 0 && (
        <section>
          <div className="mb-6 flex items-end justify-between">
            <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-primary">
              <PlayCircle className="h-6 w-6 text-gold" /> Continuar assistindo
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {continueList!.slice(0, 4).map((t: any) => (
              <TeachingCard key={t.id} teaching={t} />
            ))}
          </div>
        </section>
      )}

      {(favs?.length ?? 0) > 0 && (
        <section>
          <div className="mb-6 flex items-end justify-between">
            <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-primary">
              <Heart className="h-6 w-6 text-destructive" /> Seus favoritos
            </h2>
            <Link to="/favoritos" className="text-sm font-medium text-primary hover:underline">
              Ver todos →
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {favs!.slice(0, 4).map((t: any) => (
              <TeachingCard key={t.id} teaching={t} />
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="mb-6 flex items-end justify-between">
          <h2 className="font-serif text-2xl font-semibold text-primary">Últimos ensinos</h2>
          <Link to="/ensinos" className="text-sm font-medium text-primary hover:underline">
            Ver todos →
          </Link>
        </div>
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-64 animate-pulse rounded-xl bg-secondary" />
            ))}
          </div>
        ) : (recent?.length ?? 0) === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <p className="text-muted-foreground">Ainda não há ensinos cadastrados.</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Um administrador precisa importar os vídeos do canal do YouTube.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {recent!.map((t: any) => (
              <TeachingCard key={t.id} teaching={t} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
